/**
 * Structured Logging for UI Worker
 *
 * Provides consistent logging interface that uses Sentry when available,
 * falls back to console logging otherwise.
 *
 * @example
 * import { logError, logWarning, logInfo } from '@/lib/logger';
 *
 * logError('Failed to fetch records', { recordId, projectId });
 * logWarning('Slow API response', { duration: 1200 });
 * logInfo('User action', { action: 'upload', fileCount: 3 });
 */

import { captureException, captureMessage } from './sentry';

/**
 * Log error with context
 *
 * @param message - Error message
 * @param context - Additional context (tags, extra data)
 * @param error - Optional Error object
 */
export function logError(
  message: string,
  context?: Record<string, unknown>,
  error?: Error
): void {
  // Send to Sentry if error object provided
  if (error) {
    captureException(error, {
      ...context,
      message,
    });
    return;
  }

  // Send message to Sentry, context rides along as extras
  captureMessage(message, 'error', context ? { extra: context } : undefined);

  // Fallback to console (Sentry already handles console fallback)
  if (import.meta.env.DEV) {
    console.error(`[Error] ${message}`, context || {});
  }
}

/**
 * Log warning with context
 *
 * @param message - Warning message
 * @param context - Additional context
 */
export function logWarning(message: string, context?: Record<string, unknown>): void {
  captureMessage(message, 'warning', context ? { extra: context } : undefined);

  // Fallback to console
  if (import.meta.env.DEV) {
    console.warn(`[Warning] ${message}`, context || {});
  }
}

/**
 * Alias for logWarning (for backward compatibility)
 */
export const logWarn = logWarning;

/**
 * Log info message with context
 *
 * @param message - Info message
 * @param context - Additional context
 */
export function logInfo(message: string, context?: Record<string, unknown>): void {
  // Only log info in development
  if (import.meta.env.DEV) {
    captureMessage(message, 'info', context ? { extra: context } : undefined);
    console.log(`[Info] ${message}`, context || {});
  }
}

/**
 * Log debug message (only in development)
 *
 * @param message - Debug message
 * @param context - Additional context
 */
export function logDebug(message: string, context?: Record<string, unknown>): void {
  // Only log debug in development
  if (import.meta.env.DEV) {
    console.debug(`[Debug] ${message}`, context || {});
  }
}
